import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiAlertCircleOutline, mdiDownload, mdiOpenInNew } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { apiBaseUrl } from '../../api/client'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { useFormatters } from '../../lib/format'
import { useResidentLive } from '../../hooks/useResidentLive'
import { firstMeaningfulLine, showBrowserNotification } from '../../lib/browser-notifications'
import { BrowserNotificationsToggle } from './BrowserNotificationsToggle'
import { formatBytes, type ResidentComposerPayload } from './ResidentComposer'
import { ResidentThreadShell } from './ResidentThreadShell'
import {
    MEDIA_FAILED_BODY,
    MEDIA_PLACEHOLDER_BODIES,
    MEDIA_RAW_TYPES,
    VOICE_PLACEHOLDER_BODY,
} from './message-placeholders'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PublicRentalQuestionnaire } from '../rentalApplications/PublicRentalQuestionnaire'

/**
 * The page a resident lands on from the link in their acknowledgement e-mail
 * or the QR scan page: their request, what it produced, a place to write
 * again. Design constraints that shaped it:
 *
 *  - Mobile first, one column, no app chrome. It is read on a phone in a hall.
 *  - Works without an account. The token in the URL is the authorization,
 *    and NO Authorization header is ever sent — a stale one from
 *    localStorage would be both useless and a leak.
 *  - It is the same thread the manager reads in the queue: the messages here
 *    are exactly the ones the organization sent or received, nothing internal.
 *    A manager who happens to be logged in gets a link to the queue view.
 *
 * Since spec 2026-09-09-01 it also takes files and voice. Two rules there are
 * worth keeping in view while editing this file:
 *
 *  - every attachment URL is BUILT BY THE SERVER (the token-scoped route). The
 *    page has no credentials for the document API and must never be in a
 *    position to assemble one;
 *  - a voice message is sent as it was recorded and transcribed afterwards.
 *    There is no pre-send transcription round-trip, and the transcript is
 *    never client-supplied — it arrives on the same message, live.
 */

export interface ResidentAttachment {
    uid: string
    name: string
    mime_type?: string
    size?: number
    /** The token-scoped route, relative to the API origin. Server-built. */
    url: string
    kind: 'image' | 'audio' | 'pdf' | 'file'
}

export interface ResidentMessage {
    uid: string
    direction: 'inbound' | 'outbound'
    author: string
    body: string
    sent_at: string
    raw_type?: string
    attachments?: ResidentAttachment[]
}

export interface ResidentView {
    organization_code: string
    organization_name: string
    reference: string
    /**
     * The thread's RESOLVED name — the manager's title, else the generated
     * one, else the carrier's subject (spec 2026-09-13-02). A WhatsApp
     * resident used to read "(sans objet)" over their own request. The summary
     * is deliberately NOT exposed here: it states internal progress.
     */
    title: string
    status: 'open' | 'waiting_resident' | 'closed'
    asset_name?: string
    messages: ResidentMessage[]
    conversation_uid: string
    /** The closing answer, once given. Absent while the question is open. */
    survey?: { score: number; answered_at: string }
}

/**
 * The closing survey answer (spec 2026-09-11-01, decision 8). One call, JSON:
 * the score is the whole point, the comment is optional.
 */
async function postSurvey(orgCode: string, token: string, score: number, comment: string): Promise<ResidentView> {
    const res = await fetch(`${apiBaseUrl}/c/${encodeURIComponent(orgCode)}/${encodeURIComponent(token)}/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, comment }),
    })
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw Object.assign(new Error(err.title ?? `survey ${res.status}`), { status: res.status, code: err.code })
    }

    return res.json() as Promise<ResidentView>
}

// The stored sentinels and the raw types whose content lands later, shared
// with the manager's console: matched, never translated, never printed. See
// `message-placeholders.ts` for why they live in a single module.

async function fetchView(orgCode: string, token: string): Promise<ResidentView> {
    const res = await fetch(`${apiBaseUrl}/c/${encodeURIComponent(orgCode)}/${encodeURIComponent(token)}/state`)
    if (!res.ok) throw new Error(`state ${res.status}`)

    return res.json() as Promise<ResidentView>
}

/**
 * Send one message.
 *
 * Multipart as soon as there is anything but text, JSON otherwise — the same
 * route either way, because a photo attached to a sentence is ONE message and
 * two requests would be two messages the ledger has to reconcile.
 */
async function postMessage(
    orgCode: string,
    token: string,
    body: string,
    files: File[],
    voice: Blob | null,
): Promise<ResidentView> {
    const url = `${apiBaseUrl}/c/${encodeURIComponent(orgCode)}/${encodeURIComponent(token)}/messages`
    let init: RequestInit
    if (files.length > 0 || voice) {
        const form = new FormData()
        form.append('body', body)
        for (const file of files) form.append('files', file, file.name)
        if (voice) form.append('voice', voice, voiceFileName(voice))
        init = { method: 'POST', body: form }
    } else {
        init = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }) }
    }
    const res = await fetch(url, init)
    if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { code?: string; title?: string }
        throw Object.assign(new Error(err.title ?? `post ${res.status}`), { status: res.status, code: err.code })
    }

    return res.json() as Promise<ResidentView>
}

/** Safari records mp4/aac, everyone else webm/opus; the extension has to match
 * or the referential stores a file no player will open. */
function voiceFileName(blob: Blob): string {
    return blob.type.includes('mp4') ? 'voice-message.m4a' : 'voice-message.webm'
}

/** The absolute URL of a server-built attachment path. In production the API
 * and the page share an origin and this is the identity. */
function attachmentUrl(path: string): string {
    return path.startsWith('/') ? `${apiBaseUrl}${path}` : path
}

/** Whether this browser holds an app session. Storage may be unavailable. */
function hasLocalSession(): boolean {
    try {
        return Boolean(localStorage.getItem('authState'))
    } catch {
        return false
    }
}

function replyNotificationBody(message: ResidentMessage, t: (key: string) => string): string {
    const body = (message.body ?? '').trim()
    if (body && body !== VOICE_PLACEHOLDER_BODY && !MEDIA_PLACEHOLDER_BODIES.has(body)) {
        return firstMeaningfulLine(body)
    }
    if (message.raw_type === 'audio') return t('public.voice_message')

    return t('notifications.generic_body')
}

export function ResidentConversationPage({ orgCode, token }: { orgCode: string; token: string }) {
    const { t } = useTranslation('conversations')
    const { formatDate } = useFormatters()
    const queryClient = useQueryClient()
    const [error, setError] = useState<string | null>(null)
    const endRef = useRef<HTMLDivElement | null>(null)

    const queryKey = ['resident-conversation', orgCode, token]
    const { data: view, isLoading, error: loadError } = useQuery({
        queryKey,
        queryFn: () => fetchView(orgCode, token),
        retry: false,
    })

    const send = useMutation({
        mutationFn: (payload: ResidentComposerPayload) =>
            postMessage(orgCode, token, payload.body, payload.files, payload.voice),
        onSuccess: (next) => {
            queryClient.setQueryData(queryKey, next)
            setError(null)
        },
        onError: (e: Error & { status?: number }) => {
            if (e.status === 429) setError(t('public.too_many'))
            else if (e.status === 413) setError(t('public.too_large'))
            else if (e.status === 422) setError(e.message || t('public.send_failed'))
            else setError(t('public.send_failed'))
        },
    })

    const [surveyError, setSurveyError] = useState<string | null>(null)
    const survey = useMutation({
        mutationFn: (payload: { score: number; comment: string }) => postSurvey(orgCode, token, payload.score, payload.comment),
        onSuccess: (next) => {
            queryClient.setQueryData(queryKey, next)
            setSurveyError(null)
        },
        onError: (e: Error & { status?: number }) => {
            // Reopened meanwhile (409): the state the page shows is stale, the
            // refetch puts the composer back in front of the question.
            if (e.status === 409) void queryClient.invalidateQueries({ queryKey })
            setSurveyError(e.status === 429 ? t('public.too_many') : t('public.survey_failed'))
        },
    })

    // The composer owns the draft, the files and the recording, and keeps them
    // when a send is refused (spec 2026-09-18-03 § 6). This page only owns the
    // request and the error it produced.
    const submit = useCallback(
        (payload: ResidentComposerPayload) => send.mutateAsync(payload),
        [send],
    )

    // Live: the page is the resident's return address, so it has to move on
    // its own — a reply, a ticket changing state, or the transcript of a voice
    // message landing, all reach here with no reload (spec 2026-09-08-03 and
    // 2026-09-09-01). The server pushes the whole view through the very
    // projection the initial fetch used, so it goes straight into the cache
    // this component already reads from; there is nothing to re-fetch.
    useResidentLive(orgCode, token, (next) => {
        queryClient.setQueryData(queryKey, next as ResidentView)
    })

    const seenMessagesRef = useRef<Set<string> | null>(null)

    useEffect(() => {
        if (!view) return
        const current = new Set(view.messages.map((message) => message.uid))
        const previous = seenMessagesRef.current
        seenMessagesRef.current = current
        if (!previous) return

        const replies = view.messages.filter(
            (message) => message.direction === 'outbound' && !previous.has(message.uid),
        )
        const last = replies[replies.length - 1]
        if (!last) return

        showBrowserNotification({
            title: t('notifications.resident_title', { organization: view.organization_name }),
            body: replyNotificationBody(last, t),
            tag: 'resident-conversation',
        })
    }, [t, view])

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'end' })
    }, [view?.messages.length])

    // A manager who opened the resident's link while logged in: point them at
    // the queue, where the same thread has the assets, the tickets and the
    // composer. Detection is local — the link simply 401s for anyone else.
    const signedIn = hasLocalSession()

    if (isLoading) {
        return (
            <div className="mx-auto max-w-xl p-6 text-muted-foreground" data-testid="resident-loading">
                {t('public.loading')}
            </div>
        )
    }

    // Unknown org, unknown or deleted thread: one answer, by design.
    if (loadError || !view) {
        return (
            <div className="mx-auto max-w-xl p-6" data-testid="resident-not-found">
                <AppIcon path={mdiAlertCircleOutline} size="lg" className="mb-3" />
                <h1 className="text-xl font-semibold text-foreground">{t('public.not_found_title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{t('public.not_found_body')}</p>
            </div>
        )
    }

    const closed = view.status === 'closed'

    return (
        <ResidentThreadShell
            organizationName={view.organization_name}
            title={view.title}
            status={view.status}
            assetName={view.asset_name}
            reference={view.reference}
            empty={view.messages.length === 0}
            emptyIntro={view.messages.length === 0 ? t('composer.intro') : null}
            headerExtra={
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <BrowserNotificationsToggle size="sm" />
                    {signedIn ? (
                        <Link
                            to="/$org/conversations"
                            params={{ org: view.organization_code }}
                            search={{
                                conversation: view.conversation_uid,
                                status: 'open,waiting_resident,closed',
                                channel: undefined,
                                resolution: undefined,
                                asset: undefined,
                                unattached: undefined,
                                draft: undefined,
                                q: undefined,
                                selected: undefined,
                                reply_for_issue: undefined,
                                message: undefined,
                            }}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            data-testid="resident-open-in-console"
                        >
                            <AppIcon path={mdiOpenInNew} size="xs" />
                            {t('public.open_in_console')}
                        </Link>
                    ) : null}
                </div>
            }
            beforeComposer={
                <>
                    {closed ? (
                        <ResidentSurvey
                            answered={view.survey}
                            onAnswer={(score, comment) => survey.mutate({ score, comment })}
                            sending={survey.isPending}
                            error={surveyError}
                        />
                    ) : null}
                    <PublicRentalQuestionnaire orgCode={orgCode} token={token} />
                </>
            }
            composer={{
                onSend: submit,
                sending: send.isPending,
                error,
                notice: closed ? <p className="text-xs text-muted-foreground">{t('public.closed_hint')}</p> : null,
                bodyTestId: 'resident-reply-body',
                errorTestId: 'resident-reply-error',
            }}
        >
            {view.messages.map((message) => {
                const mine = message.direction === 'inbound'
                const isVoice = message.raw_type === 'audio'
                const body = (message.body ?? '').trim()
                const attachments = message.attachments ?? []
                const awaitingTranscript = isVoice && body === VOICE_PLACEHOLDER_BODY
                // Content the server fetches separately (spec 2026-09-09-12):
                // a photo or a voice note sent from WhatsApp is stored as a
                // row at once and its bytes land afterwards. Read as a STATE
                // — a media row of the resident's own with no attachment —
                // exactly as the console reads it. The console tells a
                // definitive failure apart by its dropped entry; this page is
                // never given one, so the failure is read off its own
                // sentinel. Either way the sentinel is not what is printed.
                const mediaAwaited = mine && MEDIA_RAW_TYPES.has(String(message.raw_type ?? '')) && attachments.length === 0
                const mediaFailed = mediaAwaited && body === MEDIA_FAILED_BODY
                const mediaPending = mediaAwaited && !mediaFailed
                // A caption is the resident's own words and always shows; a
                // stored sentinel is the server's marker and never does.
                const showBody = body !== '' && body !== VOICE_PLACEHOLDER_BODY && !MEDIA_PLACEHOLDER_BODIES.has(body)
                const stateLine = `italic opacity-80${showBody ? ' mt-1' : ''}`
                return (
                    <div
                        key={message.uid}
                        className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                        data-testid={`resident-message-${message.uid}`}
                    >
                        <div
                            className={`max-w-[85%] rounded-2xl px-3 py-2 text-[15px] shadow-sm ${
                                mine ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-background text-foreground rounded-bl-sm'
                            }`}
                        >
                            {/* The sender is named on the other side's bubbles only:
                                the right-hand side is always the resident. */}
                            {!mine && (
                                <div className="text-xs font-medium text-primary mb-0.5">
                                    {message.author || view.organization_name}
                                </div>
                            )}
                            {showBody && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
                            {/* A placeholder body is not something the resident wrote: say
                                what it is, in their language, rather than printing the
                                stored sentinel. "Being retrieved" comes first because a
                                file on its way is not a file that failed, and a voice note
                                that has landed is the only one whose transcript can be
                                said to be missing. */}
                            {mediaPending ? (
                                <p className={stateLine} data-testid="resident-media-pending">
                                    {t(`panel.media_pending_${message.raw_type}`, {
                                        defaultValue: t('panel.media_pending'),
                                    })}
                                </p>
                            ) : mediaFailed ? (
                                <p className={stateLine} data-testid="resident-media-failed">
                                    {t('public.media_failed')}
                                </p>
                            ) : awaitingTranscript ? (
                                <p className={stateLine} data-testid="resident-voice-pending">
                                    {t('public.voice_pending_transcript')}
                                </p>
                            ) : null}
                            {attachments.length > 0 && (
                                <div className="mt-2 space-y-2" data-testid="resident-message-attachments">
                                    {attachments.map((attachment) => (
                                        <ResidentAttachmentView key={attachment.uid} attachment={attachment} mine={mine} />
                                    ))}
                                </div>
                            )}
                            <div
                                className={`mt-0.5 text-right text-[11px] leading-none ${mine ? 'text-primary-foreground/75' : 'text-muted-foreground'}`}
                                data-testid="resident-message-meta"
                            >
                                {isVoice && `${t('public.voice_message')} · `}
                                {formatDate(message.sent_at, { dateStyle: 'medium', timeStyle: 'short' })}
                            </div>
                        </div>
                    </div>
                )
            })}
            <div ref={endRef} />
        </ResidentThreadShell>
    )
}

/**
 * One attachment inside a bubble.
 *
 * Everything goes through the server-supplied `url`: no `DocumentPreviewCard`
 * here — that component reads the authenticated document API, which this page
 * has no credentials for.
 */
function ResidentAttachmentView({ attachment, mine }: { attachment: ResidentAttachment; mine: boolean }) {
    const { t } = useTranslation('conversations')
    const href = attachmentUrl(attachment.url)
    const muted = mine ? 'text-primary-foreground/80' : 'text-muted-foreground'

    if (attachment.kind === 'image') {
        return (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={t('public.open_image')}
                title={attachment.name}
                data-testid="resident-attachment-image"
            >
                <img
                    src={href}
                    alt={attachment.name}
                    className="max-h-60 w-auto rounded-lg border border-black/10"
                    loading="lazy"
                />
            </a>
        )
    }

    if (attachment.kind === 'audio') {
        return (
            <audio
                controls
                src={href}
                preload="metadata"
                className="h-10 w-full max-w-[16rem]"
                data-testid="resident-attachment-audio"
            />
        )
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-lg border border-black/10 bg-background/40 px-2 py-1.5 text-xs"
            title={t('public.download')}
            data-testid="resident-attachment-file"
        >
            <AppIcon path={SECTION_ICONS.documents} size="sm" className={`flex-shrink-0 ${muted}`} />
            <span className="truncate max-w-[12rem]">{attachment.name}</span>
            {attachment.size ? <span className={`flex-shrink-0 ${muted}`}>{formatBytes(attachment.size)}</span> : null}
            <AppIcon path={mdiDownload} size="xs" className={`flex-shrink-0 ${muted}`} />
        </a>
    )
}

/**
 * The closing question — "would you recommend…", 0 to 10 — asked once the
 * request is closed (spec 2026-09-11-01, decision 8). The NPS is measured
 * here and nowhere else on this page; once answered, the block only says so.
 */
function ResidentSurvey({
    answered,
    onAnswer,
    sending,
    error,
}: {
    answered?: { score: number; answered_at: string }
    onAnswer: (score: number, comment: string) => void
    sending: boolean
    error: string | null
}) {
    const { t } = useTranslation('conversations')
    const [score, setScore] = useState<number | null>(null)
    const [comment, setComment] = useState('')

    if (answered) {
        return (
            <section className="px-4 md:px-6 py-3 border-t text-sm text-muted-foreground" data-testid="resident-survey-thanks">
                {t('public.survey_thanks', { score: answered.score })}
            </section>
        )
    }

    return (
        <section className="px-4 md:px-6 py-4 border-t space-y-3" data-testid="resident-survey">
            <p className="text-sm font-medium text-foreground">{t('public.survey_question')}</p>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('public.survey_question')}>
                {Array.from({ length: 11 }, (_, value) => (
                    <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={score === value}
                        onClick={() => setScore(value)}
                        className={`h-9 w-9 rounded-md border text-sm tabular-nums transition-colors ${
                            score === value
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background text-foreground hover:bg-muted'
                        }`}
                        data-testid={`resident-survey-score-${value}`}
                    >
                        {value}
                    </button>
                ))}
            </div>
            <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>{t('public.survey_low')}</span>
                <span>{t('public.survey_high')}</span>
            </div>
            <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('public.survey_comment')}
                aria-label={t('public.survey_comment')}
                rows={2}
                maxLength={2000}
                className="text-sm"
                data-testid="resident-survey-comment"
            />
            {error && (
                <p className="text-xs text-destructive" data-testid="resident-survey-error">
                    {error}
                </p>
            )}
            <Button
                size="sm"
                disabled={score === null || sending}
                onClick={() => score !== null && onAnswer(score, comment)}
                data-testid="resident-survey-send"
            >
                {t('public.survey_send')}
            </Button>
        </section>
    )
}
